/* ============================================================================
   pdp-desk-3col.js — the three-column product page. DESKTOP ONLY.

   What Dorian asked for (2026-08-30, in the order he asked it):
     • the left becomes TWO media columns, so the page reads as three columns
     • the page uses the SCREEN, not a 1260px column in the middle of it
     • one scrollbar on the page, not one per column
     • both columns scroll TOGETHER; when the description is spent, the feed
       goes on alone
       (this last one replaced two earlier answers - see the router below)

   HOW IT IS BUILT, and what it deliberately does NOT touch:

   Only ONE element moves: the feed section. It is our own, and
   agilite-media-flow.js finds it by [data-agmf] wherever it sits, so moving it
   into the product card's column costs nothing. The theme's product-info stays
   exactly where the theme put it — moving it would drop every `.product
   .product-info` rule on the floor and take the variant picker's styling with
   it. Everything else here is CSS on elements that stay put.

   THE SCROLL. Two panes, each its own scroller, sized to the viewport LESS a
   sliver of footer, so the document keeps only that sliver to scroll. The
   footer keeps the wheel that is over the footer (2026-09-02); everything
   else - the grid, and the gutters either side of it - drives the panes.
   A wheel router
   then applies every scroll to BOTH panes, so they travel together until the
   description runs out and the feed goes on alone. None of this can be had
   from `position: sticky`: sticky pins the SHORTER column while the taller
   one moves, and here the taller one is the endless feed — exactly backwards.

   REVERSIBLE. Below the width threshold, or if anything it needs is missing,
   it undoes itself completely and the page is the page it was. The mobile
   layout is not merely unstyled by this file, it is never entered.
   ============================================================================ */
(function () {
  'use strict';

  if (window.AGDesk3) return;

  // Three columns need room for two media columns AND the buy column beside
  // them. Below this the page stays as it is - two columns, then one.
  // ── TWO BANDS, NOT ONE (Dorian 2026-09-07, on an iPad Pro at 1024:
  // "the feed right under the product card, one column only, and keep the
  // right side sticky like in regular desktop").
  //
  // MIN_WIDTH is where the ARRANGEMENT starts: two panes, the buy column
  // sticky beside them, and the feed lifted out from under the whole product
  // block into the media pane where it belongs. That is what he is asking
  // for, and it wants nothing wider than about a thousand pixels.
  //
  // WIDE_WIDTH is where the desktop FEED GRID starts, and it has to stay at
  // 1200 because two things there are not layout, they are a different
  // product: the feed draws in two columns, and the swipeable rails are
  // dismantled outright - clones hidden, every slide after the first hidden,
  // the track made unscrollable and its arrows removed (see the block in
  // pdp-desk-3col.css). Bringing that down to 1000 would silently drop six
  // of the seven pieces each rail carries. So the arrangement comes down and
  // the dismantling does not.
  var MIN_WIDTH = 1000;
  var WIDE_WIDTH = 1200;
  var mq = window.matchMedia('(min-width:' + MIN_WIDTH + 'px)');
  var wideMq = window.matchMedia('(min-width:' + WIDE_WIDTH + 'px)');
  function syncWide() {
    document.documentElement.classList.toggle('agdesk-wide', wideMq.matches);
  }
  syncWide();
  if (wideMq.addEventListener) wideMq.addEventListener('change', syncWide);
  else if (wideMq.addListener) wideMq.addListener(syncWide);

  var state = null;   // {left, right, feed, feedHome, cleanup}

  function log() {
    try {
      if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
        console.log.apply(console, ['[agdesk3]'].concat([].slice.call(arguments)));
      }
    } catch (e) {}
  }

  /* WHERE THE PANES ACTUALLY START.
     This was the header's height, and the header is not the only thing above
     the product. Measured on the live page: header 64px, but `.product` starts
     at y=118 - an announcement bar (39px, and NOT sticky) and the section's own
     16px of padding sit in between. So panes sized `100vh - 63px` began at 118
     and ended 55px past the fold: the last strip of BOTH columns was rendered
     permanently off-screen, and once their scrollbars were hidden there was
     nothing left to hint that it was there.

     Measure the element itself rather than guessing at its ancestors. `top +
     scrollY` is its offset from the top of the DOCUMENT, so this is correct
     whatever the page happens to be scrolled to when it runs. */
  function paneTop(grid) {
    if (!grid) return 0;
    return Math.max(0, Math.round(grid.getBoundingClientRect().top + window.scrollY));
  }

  function parts() {
    var hero = document.querySelector('.agmf-hero');
    var feed = document.querySelector('section.agmf-wrapper, section[class*="agmf-wrapper"]');
    var main = document.querySelector('.shopify-section--main-product');
    if (main) {
      var grid = main.querySelector('.product');
      var info = main.querySelector('safe-sticky.product-info') ||
                 main.querySelector('.product-info');
      if (grid && hero && info && feed) return { main: main, grid: grid, hero: hero, info: info, feed: feed };
    }
    /* THE GEAR BUILDER'S PAGE (K-Zero SF, Dorian 2026-10-01: everything the
       Magnetix page does on desktop, "the exact same way"). Its section is
       main-product-agb-system: no .product grid but .agb-layout, the product
       card inside the builder's left column #summary-panel, and the buy column
       .agb-main. The left PANE here is that whole column - the card, the
       builder's tools and its live build (which takes the card's place while
       the shopper builds, agmf-builder-swap.js) - with the feed below it, so
       the card, the build and the feed scroll as the one thing the media pane
       is on the belt. Everything after this function is unchanged: enter()
       wraps `hero` and the feed in .agdesk-media where `hero` stood, and the
       router, the footer strip and the header all key off agdesk-on. */
    var panel = document.getElementById('summary-panel');
    var layout = panel && panel.parentElement;
    var buy = layout && layout.querySelector(':scope > .agb-main');
    if (panel && layout && layout.classList.contains('agb-layout') && buy &&
        hero && panel.contains(hero) && feed) {
      return { main: layout, grid: layout, hero: panel, info: buy, feed: feed };
    }
    return null;
  }

  /* ---- the scroll router -------------------------------------------------
     THE TWO COLUMNS MOVE TOGETHER (Dorian 2026-08-30):

       - every scroll, up or down, moves the pictures AND the description by
         the same pixels
       - when the description has no more to give, the feed carries on alone

     Two earlier shapes, and why they went. A FIXED order - description first
     going down, feed first coming up - meant one column sat still while the
     other moved, so the page never felt like one page. POINTER-ANCHORED -
     whichever column the cursor was over - fixed that but made the page
     depend on where the mouse happened to be resting, which is not something
     a reader is aware of choosing.

     Together is the simplest thing that is true of the page: there is a
     fixed amount to READ beside an endless amount to LOOK AT, so they travel
     as one until the short one is spent.

     The cost, taken deliberately: this drives both scrollers by hand, so the
     browser's own smoothing and trackpad inertia are gone. They cannot be
     kept - native scrolling moves the one element under the pointer, and
     lockstep is the opposite of that. What IS kept is a scroller nested
     inside a pane: it is asked first, every time. */
  function canScroll(el, dy) {
    if (!el) return false;
    var max = el.scrollHeight - el.clientHeight - 1;
    return dy > 0 ? el.scrollTop < max : el.scrollTop > 0;
  }

  // While a body-level sheet is open it owns the page, and it says so with
  // this class for exactly as long as it is up. Cheaper and more honest than
  // naming each overlay: a new one is covered the day it is written.
  function overlayOpen() {
    return document.documentElement.classList.contains('agmf-noscroll');
  }

  var FOOTER_SEL = 'footer, .shopify-section-group-footer-group';

  function overFooter(target) {
    return !!(target && target.closest && target.closest(FOOTER_SEL));
  }

  /* ---- THE DOCUMENT'S SCROLL IS THE FOOTER'S STATE -----------------------
     (Dorian 2026-09-16: "prevent scrolling the footer except if the mouse is
     really in the black zone already".)

     The panes are sized to the viewport less the peek, so the document's ONLY
     remaining scroll is the tucked footer: moving the document down IS opening
     the footer. The wheel router above can refuse a wheel, but it cannot
     refuse the other ways the document moves - dragging the 15px scrollbar on
     the right edge is a native scroll with no cancellable event at all, which
     is the one Dorian hit ("the side right bar on right side of the screen"),
     and Page Down does the same.

     So the position is guarded rather than the gesture: the document may only
     move DOWN while the pointer is genuinely inside the footer, or while the
     keyboard focus is (tabbing into the footer's links scrolls them into view,
     and a shopper who cannot use a mouse must still reach them). Moving UP is
     always allowed - that is closing it, and the way home must never be
     gated.

     Reverting from inside the scroll handler is self-settling: the revert
     fires one more scroll event, that one reads equal to the sanctioned
     position, and it stops. No flag, so there is no flag to get stuck. */
  var docY = 0;                       // the sanctioned document position
  var ptr = { x: -1, y: -1 };
  var sanctionUntil = 0;              // the peek's own smooth scroll, after a click

  /* ASKED OF THE DOM, NOT OF A RECTANGLE. agmf-footer-peek.js appends its
     chevron INSIDE the footer (`inner.appendChild(cue)`) but sits it on the
     strip's top EDGE, so a click on it can land a pixel above the footer's
     own box. A hit test answers "is the thing under the pointer part of the
     footer", which is the question being asked, and it also answers the
     scrollbar correctly: elementFromPoint over a scrollbar is null, and null
     is not the footer. */
  function pointerInFooter() {
    if (ptr.x < 0) return false;
    var el = document.elementFromPoint(ptr.x, ptr.y);
    if (el && el.closest && el.closest(FOOTER_SEL)) return true;
    /* AND THE FOOTER'S OWN BOX, even when something is lying over it. In the
       Shopify PREVIEW an iframe for the preview bar covers the strip, so the
       hit test above can never answer "footer" there and the handle would be
       dead in exactly the place Dorian tests it. The box is still the black
       zone: the pointer cannot be below the viewport, so this is the visible
       strip. It does NOT let the scrollbar back in - the footer's box ends at
       the document's client width (1905 here) and the scrollbar lives in the
       15px beyond it. */
    var f = document.querySelector(FOOTER_SEL);
    if (!f) return false;
    var r = f.getBoundingClientRect();
    return ptr.x >= r.left && ptr.x <= r.right && ptr.y >= r.top && ptr.y <= r.bottom;
  }

  function focusInFooter() {
    var a = document.activeElement;
    return !!(a && a.closest && a.closest(FOOTER_SEL));
  }

  function trackPointer(e) { ptr.x = e.clientX; ptr.y = e.clientY; }

  /* A CLICK ON THE STRIP IS A DECISION, AND IT OUTLIVES THE POINTER.
     The peek opens with `scrollTo({behavior:'smooth'})` and gives itself 900ms
     to land; a shopper who clicks and then moves the mouse off the strip would
     otherwise have the rest of that glide reverted under them. So a click
     anywhere in the footer sanctions the next second of scrolling. */
  function sanctionClick(e) {
    if (overFooter(e.target)) sanctionUntil = Date.now() + 1200;
  }

  /* Coming back with the back button restores the offset the way a reload
     does, and a bfcache restore runs no boot code at all - so it is asked
     for here rather than assumed. */
  function onPageShow(e) {
    if (!e.persisted) return;
    if (window.pageYOffset) window.scrollTo(0, 0);
    docY = 0;
  }

  function guardScroll() {
    var y = window.pageYOffset;
    if (y > docY + 0.5 && Date.now() > sanctionUntil &&
        !(pointerInFooter() || focusInFooter())) {
      window.scrollTo(0, docY);
      return;
    }
    docY = y;
  }

  /* ANY scroller between the wheel and us is asked first - the article
     reader, the cart drawer, a menu that overflows - because once both panes
     have taken the delta it is too late to give it back.

     Walking UP from the target also answers the gutter question, which is why
     this replaced the old pane-and-nested pair. Reaching a pane means the
     wheel is ours by position; reaching the body without having found a
     scroller means it is ours by default - and that second case IS the
     gutter, the header strip, and every other bit of page that is not one of
     the three columns. The old code asked 'is this inside the grid?', which
     answered the gutter with a no and sent 300px of either side to the
     document. */
  /* THE EVENT'S WHOLE PATH, SHADOW ROOTS INCLUDED. The theme's modals keep
     their scrolling box inside a shadow root (x-modal: the size chart), which
     parentNode never reaches - walking up from the chart went straight to the
     body, the router took the wheel and the panes scrolled behind the open
     chart (Dorian 2026-10-22). composedPath() crosses into the shadow tree. */
  function eventPath(e) {
    var path = (e.composedPath && e.composedPath()) || [];
    if (!path.length) {
      for (var n = e.target; n; n = n.parentNode) path.push(n);
    }
    return path;
  }

  function scrollerTakesIt(e, dy) {
    var path = eventPath(e);
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (!el || el.nodeType !== 1) continue;
      if (el === document.body || el === document.documentElement) return false;
      if (el === state.left || el === state.right) return false;   // ours
      var oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && canScroll(el, dy)) return true;
    }
    return false;
  }

  /* An open modal or drawer owns the wheel over it - its own box scrolls, or,
     when there is nothing left to scroll, nothing does. Never the panes behind
     it. */
  var OPEN_OVERLAY_SEL = 'x-modal[open], x-drawer[open], x-popover[open], dialog[open], [aria-modal="true"]';
  function inOpenOverlay(e) {
    var path = eventPath(e);
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.nodeType === 1 && el.matches && el.matches(OPEN_OVERLAY_SEL)) return true;
    }
    return false;
  }

  function route(e) {
    if (!state) return;
    var dy = e.deltaY;
    if (!dy) return;
    // deltaMode 1 = lines, 2 = pages. Wheel events on Firefox arrive in lines
    // and a raw deltaY of 3 would move the page by three pixels.
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= state.right.clientHeight;

    /* THREE DESTINATIONS, NOT TWO (Dorian 2026-09-02):

         "i want also that not matter where we scroll it makes the
          gallery/description scroll (now the edges are making the whole page
          scroll to the bottom and not those 2 elements). But I want to have
          the footer showing up a bit on the bottom and if we scroll on it
          then we can see it more"

       The two halves only hold together. 'Everything drives the panes' on its
       own makes the footer unreachable - the feed is endless, so a downward
       wheel over a pane never runs out and never reaches the page below. So
       the footer keeps the wheel that is over the footer, and the sliver of
       it left showing (--agdesk-footer-peek) is what makes that a handle
       rather than a dead edge.

       This replaced `closest('.product.agdesk-grid')`, one test that was
       quietly doing three jobs. Only one was about the grid; the other two
       are now named, because widening the net without naming them would have
       brought back exactly the three things that test was protecting:

         - body-level overlays scrolling the page behind their own scrim. The
           press-article reader and the read-more sheet hang off document.body,
           so no ancestor test involving the grid ever saw them; what actually
           saved them was that they are not inside it. Now overlayOpen() does
           it deliberately, and scrollerTakesIt() catches anything that scrolls.
         - the footer being unreachable by wheel. It has its own branch now.
         - never getting back once the document had moved down, an upward wheel
           spending every notch rewinding the invisible feed. The document is
           asked first on the way UP, below. */
    if (overlayOpen()) return;                    // a sheet owns the page
    if (inOpenOverlay(e)) return;                 // so does an open modal (size chart)
    if (overFooter(e.target)) return;             // the footer keeps its own wheel
    if (scrollerTakesIt(e, dy)) return;           // something nearer was here first

    /* COMING BACK UP. The document's only remaining scroll IS the footer, so
       anything it is holding is footer that has been pulled up over the page.
       Give that back before the panes take a single pixel, or the way home
       from the footer is a scrollbar drag. Clamped to what is actually there
       so one hard flick lands on the product rather than overshooting. */
    if (dy < 0 && window.scrollY > 0) {
      window.scrollBy(0, Math.max(dy, -window.scrollY));
      e.preventDefault();
      return;
    }

    /* WHAT SITS ABOVE THE PANES SCROLLS LIKE THE TOP OF A PAGE (Dorian
       2026-10-12: the "SALE ENDS IN" countdown "needs to be not sticky and
       slide with the page"). agmf-desktop-header.js owns that strip, as it
       owns the header: going down it takes the wheel until the strip is gone,
       coming up it gives it back only once both panes are at their top. The
       panes get whatever is left of the wheel. */
    var hdr = window.AGMFDesktopHeader;
    if (hdr && hdr.take) {
      dy = hdr.take(dy, !(state.left.scrollTop > 0) && !(state.right.scrollTop > 0));
      if (!dy) { e.preventDefault(); return; }
    }

    // BOTH COLUMNS MOVE TOGETHER, the same pixels, for as long as both can.
    // The description is the short one so it runs out first, and the feed
    // carries on alone - which is the shape of the page itself: a fixed
    // amount to read beside an endless amount to look at.
    //
    // No `return` between the two: this is not a router picking a winner any
    // more, it is one scroll applied to two scrollers. Whichever still has
    // room takes it.
    var moved = false;
    if (canScroll(state.right, dy)) { state.right.scrollTop += dy; moved = true; }
    if (canScroll(state.left, dy))  { state.left.scrollTop  += dy; moved = true; }
    /* AND WHEN NEITHER CAN MOVE, THE WHEEL STOPS HERE (Dorian 2026-09-16:
       "dont allow that if scrolling on the right side of the screen it makes
       the footer scroll").

       This used to preventDefault only when a pane had actually taken the
       delta, and let the rest fall through on the reasoning that "the page
       below the panes may have it". The page below the panes is the tucked
       FOOTER - it is the document's only remaining scroll - so that line was
       quietly a second way to open it, from a wheel that was never aimed at
       it. It shows on the right first because that is the column that runs
       out: measured on the belt page, the buy column is spent after 1,440px
       while the feed's runway is 26,550px, so the two are only spent together
       in the moment before the feed extends itself. Measured with both panes
       driven to their end: a wheel over the buy column, over the gutter
       beside it and over the feed were all uncancelled, and each one pulled
       the footer up.

       The footer is not cut off by this - it keeps the branch above, so a
       wheel ON the strip still opens it, which is the handle it was given on
       2026-09-02 along with its chevron and its click. What is gone is
       opening it from somewhere else. The document scrollbar and the keyboard
       are untouched; this is the wheel only. */
    e.preventDefault();
  }

  /* ---- keep the feed alive -----------------------------------------------
     Everything the feed does off a scroll - attaching a video's source, then
     PLAYING it, and extending the runway with new cycles - is bound to the
     WINDOW's scroll event. This layout takes the scroll away from the window
     and gives it to two panes, so none of that fired: videos never attached
     and therefore never autoplayed, and the feed stopped growing. Forwarding
     the pane's scroll to the feed's own tick is the whole fix. rAF-throttled,
     because a pane scroll fires far more often than a page one. */
  var ticking = false;
  function feedTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      try { if (window.AGMF && window.AGMF.tick) window.AGMF.tick(); } catch (e) {}
    });
  }

  function enter() {
    if (state) return;
    var p = parts();
    if (!p) { log('the page is missing a piece - staying as it is'); return; }

    var setTop = function () {
      document.documentElement.style.setProperty('--agdesk-top', paneTop(p.grid) + 'px');
    };

    // remember exactly where the feed came from, so leaving puts it back
    var feedHome = { parent: p.feed.parentNode, next: p.feed.nextSibling };

    var left = document.createElement('div');
    left.className = 'agdesk-media';
    p.hero.parentNode.insertBefore(left, p.hero);
    left.appendChild(p.hero);
    left.appendChild(p.feed);

    p.grid.classList.add('agdesk-grid');
    p.info.classList.add('agdesk-buy');
    document.documentElement.classList.add('agdesk-on');

    /* MEASURE AFTER THE CLASS, NOT BEFORE (2026-09-02).
       setTop() used to run at the top of enter(), which is too early by one
       line: `agdesk-on` is ITSELF a layout change - agilite-media-flow.css
       zeroes the product section's top padding under it - and so is moving
       the hero and the feed into the media pane just above. Measuring first
       recorded a position the page was about to leave: --agdesk-top read 159
       where the grid then sat at 143, and everything downstream inherited
       the 16px. The panes ran 16px short, so the folded footer strip showed
       60px where the peek asks for 44.

       The fix is the ordering, not a correction factor - nothing between the
       top of enter() and here reads --agdesk-top, so the measurement simply
       belongs after the last thing that moves the grid.

       SECOND WRITER: agmf-desktop-header.js also writes --agdesk-top - 0
       while the header is hidden, re-measured after the 320ms slide when it
       comes back - using this same `grid rect top + scrollY` formula. The
       two agree only for as long as that stays true, so if this formula
       changes, that file changes with it. */
    setTop();

    state = { left: left, right: p.info, feed: p.feed, feedHome: feedHome,
              hero: p.hero, grid: p.grid, setTop: setTop };

    /* A RELOAD MUST NOT ARRIVE WITH THE FOOTER OPEN (Dorian 2026-09-16: "a
       lot of times when reloading the page it puts the footer already open
       fix it it should stay closed").

       `history.scrollRestoration` is 'auto' by default, so the browser puts
       the document back where it was - and on this layout "where it was" is
       how far the footer had been pulled up. Reproduced: scrolled to 400,
       reloaded, landed at 400 with the footer showing. Manual restoration
       stops the next reload; the scrollTo below handles THIS one, whose
       restore has already happened by the time this runs.

       There is a second half to the same bug: at load the document is far
       taller than the ~504px it ends up with - the reviews app section is
       still inline until agilite-media-flow.js moves it into the accordion,
       and the feed is still inline until enter() lifts it into the media pane
       just above. So the browser restores a LARGE offset, the document then
       shrinks under it, and the offset clamps to the bottom: the footer wide
       open. That is why it happened "a lot of times" rather than only after
       scrolling to the footer.

       THE RESET IGNORES A HASH, deliberately. It was written as
       `!location.hash` at first, which reads as respecting a deep link - but
       on this layout the document's only scroll IS the footer, so a fragment
       jump cannot reach anything except the footer. Worse, theme.liquid's
       skip-to-content link is `href="#main" allow-hash-change`, a native
       fragment jump that leaves `#main` in the address bar; once it was there
       this reset would have been skipped on every reload from then on, and
       the footer would be open again. Anything a shopper deep-links to lives
       in a pane, and the panes are not the document.

       Both are undone in leave(), so a window narrowed back below the
       threshold is an ordinary page again, restoring its position the way
       every other page on the site does. */
    try {
      if ('scrollRestoration' in history) {
        state.priorRestoration = history.scrollRestoration;
        history.scrollRestoration = 'manual';
      }
    } catch (e) {}
    if (window.pageYOffset) window.scrollTo(0, 0);
    docY = window.pageYOffset;
    ptr.x = ptr.y = -1;

    window.addEventListener('wheel', route, { passive: false });
    window.addEventListener('scroll', guardScroll, { passive: true });
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('pointermove', trackPointer, { passive: true });
    document.addEventListener('mousemove', trackPointer, { passive: true });
    document.addEventListener('click', sanctionClick, true);
    // Nothing recomputed --agdesk-top after the first pass, so a window
    // resize, a wrapping menu or a dismissed announcement bar left the panes
    // sized for a page that had moved underneath them.
    window.addEventListener('resize', setTop);
    left.addEventListener('scroll', feedTick, { passive: true });
    feedTick();                     // the first screenful, without waiting
    log('three columns on; both columns scroll together');
  }

  function leave() {
    if (!state) return;
    try {
      if ('scrollRestoration' in history && state.priorRestoration)
        history.scrollRestoration = state.priorRestoration;
    } catch (e) {}
    window.removeEventListener('scroll', guardScroll);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('pointermove', trackPointer);
    document.removeEventListener('mousemove', trackPointer);
    document.removeEventListener('click', sanctionClick, true);
    window.removeEventListener('wheel', route);
    window.removeEventListener('resize', state.setTop);
    state.left.removeEventListener('scroll', feedTick);
    // put the feed back before the wrapper goes, or it leaves with it
    state.feedHome.parent.insertBefore(state.feed, state.feedHome.next);
    state.left.parentNode.insertBefore(state.hero, state.left);
    state.left.remove();
    state.grid.classList.remove('agdesk-grid');
    state.right.classList.remove('agdesk-buy');
    document.documentElement.classList.remove('agdesk-on');
    state = null;
    log('back to the page the theme renders');
  }

  function sync() { if (mq.matches) enter(); else leave(); }

  window.AGDesk3 = {
    on: function () { return !!state; },
    enter: enter,
    leave: leave,
    _debug: function () {
      if (!state) return null;
      var r = state.right, l = state.left;
      return {
        rightScrollable: Math.max(0, r.scrollHeight - r.clientHeight),
        rightAt: Math.round(r.scrollTop),
        leftScrollable: Math.max(0, l.scrollHeight - l.clientHeight),
        leftAt: Math.round(l.scrollTop)
      };
    }
  };

  // The hero is built after the feed loads, so wait for it rather than for
  // DOMContentLoaded - at that point there is no product card to move.
  //
  // IN THE SAME TURN AS THE REVEAL (Dorian 2026-10-06: "why it always load
  // the old page and then only the new"). A 250 ms poll alone let the new page
  // paint first in the theme's two-column layout - one tall card where the
  // gallery was, the feed under the whole product - and jump into three
  // columns 110-812 ms later (measured on the preview). The hero commits by
  // adding agmf-hero-live to <html>; watching that class runs enter() in the
  // same microtask checkpoint as the hold's release, so the two-column frame
  // is never painted. The poll stays for anything the observer cannot see.
  var tries = 0, settled = false, poll = 0, watch = null;
  function settle(found) {
    if (settled) return;
    settled = true;
    clearTimeout(poll);
    if (watch) watch.disconnect();
    if (found) sync();
    try { mq.addEventListener('change', sync); }
    catch (e) { mq.addListener(sync); }
  }
  if (window.MutationObserver) {
    watch = new MutationObserver(function () { if (parts()) settle(true); });
    watch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }
  (function wait() {
    if (settled) return;
    if (parts()) { settle(true); return; }
    if (++tries < 80) { poll = setTimeout(wait, 250); return; }
    settle(false);
  })();
})();
